/**
 * Fusion demo UI store — ISOLATED from the crypto Workbench.
 * Persist key 'fusion-ui:demo' (NOT the shared 'workbench-*' keys). Holds UI prefs +
 * selection + the ingestion-replay step; the graph elements come from the seed.
 * See MULTIMODAL_WORKBENCH_STRATEGY.md §5 (own store namespace).
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { FusionModule } from '../types/fusion';
import { MAX_STEP } from '../data/fusionSeed';

export interface FusionSelection { id: string; kind: 'node' | 'edge'; }

/** A Case Canvas job (operator/evidence run) shown in the Run Rail. */
export interface CanvasJob {
  id: string;
  operator: string;             // registry id (O1, E1, …)
  label: string;
  targetId?: string;
  status: 'queued' | 'running' | 'done' | 'error';
  message?: string;
  ts: number;
}

/** Radial-menu target — where the operator ring is anchored on the canvas. */
export interface RadialTarget { x: number; y: number; nodeId: string; nodeType: string; module: string; }

/** Autopilot playbook state — the cinematic lower-third. */
export interface PlaybookState { running: boolean; title: string; sub: string; actIndex: number; totalActs: number; }

const ALL_MODULES: FusionModule[] = ['crypto', 'bank', 'company', 'social', 'network', 'shared'];

interface FusionState {
  courtSafe: boolean;
  showDemo: boolean;            // show the synthetic demo seed; OFF = only user-added data
  showLegend: boolean;          // floating Graph Legend (Workbench parity) — not persisted
  timelineExpanded: boolean;    // full ingestion timeline panel — not persisted
  moduleFilters: Record<FusionModule, boolean>;
  selected: FusionSelection | null;
  step: number;                 // ingestion stage currently shown (1..MAX_STEP)
  playing: boolean;             // replay running
  jobs: CanvasJob[];
  radial: RadialTarget | null;
  pb: PlaybookState | null;
  setShowDemo: (v: boolean) => void;
  setShowLegend: (v: boolean) => void;
  setTimelineExpanded: (v: boolean) => void;
  setCourtSafe: (v: boolean) => void;
  toggleCourtSafe: () => void;
  toggleModule: (m: FusionModule) => void;
  select: (sel: FusionSelection | null) => void;
  resetFilters: () => void;
  setStep: (n: number) => void;
  setPlaying: (p: boolean) => void;
  replay: () => void;           // restart the workflow from step 1 and play
  addJob: (j: CanvasJob) => void;
  updateJob: (id: string, patch: Partial<CanvasJob>) => void;
  clearJobs: () => void;
  openRadial: (t: RadialTarget) => void;
  closeRadial: () => void;
  setPb: (p: PlaybookState | null) => void;
}

const allOn = () => ALL_MODULES.reduce((a, m) => { a[m] = true; return a; }, {} as Record<FusionModule, boolean>);

export const useFusionStore = create<FusionState>()(
  persist(
    (set) => ({
      courtSafe: false,
      showDemo: true,
      showLegend: false,
      timelineExpanded: false,
      moduleFilters: allOn(),
      selected: null,
      step: 1,                   // start at stage 1 (bank surface only) so the ingestion
                                 // build-up is visible immediately; the timeline walks 1..MAX_STEP.
                                 // NOT persisted (partialize omits it) — every load starts fresh.
      playing: false,
      jobs: [],
      radial: null,
      pb: null,
      setShowDemo: (v) => set({ showDemo: v }),
      setShowLegend: (v) => set({ showLegend: v }),
      setTimelineExpanded: (v) => set({ timelineExpanded: v }),
      setCourtSafe: (v) => set({ courtSafe: v }),
      toggleCourtSafe: () => set((s) => ({ courtSafe: !s.courtSafe })),
      toggleModule: (m) => set((s) => ({ moduleFilters: { ...s.moduleFilters, [m]: !s.moduleFilters[m] } })),
      select: (sel) => set({ selected: sel }),
      resetFilters: () => set({ moduleFilters: allOn(), courtSafe: false }),
      setStep: (n) => set({ step: Math.max(1, Math.min(MAX_STEP, n)) }),
      setPlaying: (p) => set((s) => ({ playing: p, ...(p ? { timelineExpanded: true } : {}) })),
      replay: () => set({ step: 1, playing: true, selected: null, timelineExpanded: true }),
      addJob: (j) => set((s) => ({ jobs: [j, ...s.jobs].slice(0, 40) })),
      updateJob: (id, patch) => set((s) => ({ jobs: s.jobs.map((j) => (j.id === id ? { ...j, ...patch } : j)) })),
      clearJobs: () => set({ jobs: [] }),
      openRadial: (t) => set({ radial: t }),
      closeRadial: () => set({ radial: null }),
      setPb: (p) => set({ pb: p }),
    }),
    {
      name: 'fusion-ui:demo',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ courtSafe: s.courtSafe, showDemo: s.showDemo, moduleFilters: s.moduleFilters }),
    },
  ),
);

export { ALL_MODULES };
